import { useEffect, useState, useRef } from 'react';
import type { WheelEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../Store/hooks';
import { updateCoachProfile } from '../Store/StudentSlices/updateCoachProfileSlice';
import { getCoachInfo } from '../Store/StudentSlices/getCoachSlice';
import {
  uploadProfileImage,
  resetProfileImageUpload,
} from '../Store/StudentSlices/uploadProfileImageSlice';
import UploadModal from '../Component/Settings/UploadModal';

type UploadState = {
  isOpen: boolean;
  file: File | null;
  error: string;
  previewUrl: string;
  zoom: number;
  isDragging: boolean;
  dragOffset: { x: number; y: number };
  baseImageSize: { width: number; height: number };
};

const OnboardingSetup = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  // Initialize step from query parameter or localStorage
  const getInitialStep = () => {
    const params = new URLSearchParams(window.location.search);
    const stepParam = params.get('step');
    if (stepParam && !isNaN(Number(stepParam))) {
      return Math.min(4, Math.max(1, Number(stepParam)));
    }
    const savedStep = localStorage.getItem('onboarding_step');
    return savedStep ? Math.min(4, Math.max(1, Number(savedStep))) : 1;
  };

  // Initialize formData from localStorage
  const getInitialFormData = () => {
    const saved = localStorage.getItem('onboarding_formData');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return {
          firstName: '',
          lastName: '',
          phone: '',
          location: '',
          areaOfExpertise: '',
          expertiseChips: [] as string[],
          experienceLevel: '',
          bio: '',
          avatar: '',
        };
      }
    }
    return {
      firstName: '',
      lastName: '',
      phone: '',
      location: '',
      areaOfExpertise: '',
      expertiseChips: [] as string[],
      experienceLevel: '',
      bio: '',
      avatar: '',
    };
  };

  const [currentStep, setCurrentStep] = useState(getInitialStep);
  const [stepError, setStepError] = useState<string | null>(null);
  const [formData, setFormData] = useState(getInitialFormData);

  // Upload state matching Settings pattern
  const [uploadState, setUploadState] = useState<UploadState>({
    isOpen: false,
    file: null,
    error: '',
    previewUrl: '',
    zoom: 1,
    isDragging: false,
    dragOffset: { x: 0, y: 0 },
    baseImageSize: { width: 0, height: 0 },
  });

  // OAuth state
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);
  const [oauthError, setOauthError] = useState<string | null>(null);

  const updateUploadState = (patch: Partial<UploadState>) => {
    setUploadState((prev) => ({ ...prev, ...patch }));
  };

  // Update URL and localStorage when step changes
  useEffect(() => {
    window.history.replaceState({}, '', `/onboarding?step=${currentStep}`);
    localStorage.setItem('onboarding_step', String(currentStep));
  }, [currentStep]);

  // Save formData to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('onboarding_formData', JSON.stringify(formData));
  }, [formData]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const previewImageRef = useRef<HTMLImageElement | null>(null);

  const { loading } = useAppSelector((state) => state.updateCoachProfile);
  const { coachInfo } = useAppSelector((state) => state.getCoach);
  const {
    loading: isProfileImageUploading,
    error: hasProfileImageUploadError,
  } = useAppSelector((state) => state.uploadProfileImage);

  const expertiseOptions = [
    'Private Equity',
    'Investment Banking',
    'Hedge Funds',
    'Accounting',
    'Venture Capital',
    'Real Estate',
    'Business',
    'Information Technology',
    'Data Science',
    'Artificial Intelligence',
  ];

  const experienceLevels = ['Beginner', 'Intermediate', 'Advanced'];

  // Handle OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    const returnStep = sessionStorage.getItem('oauthReturnStep');

    if (status === 'success' && returnStep) {
      setCurrentStep(parseInt(returnStep));
      sessionStorage.removeItem('oauthReturnStep');
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Auto-fill form with existing coach data on component mount
  useEffect(() => {
    if (coachInfo) {
      const coach = coachInfo as any;
      const expertise = coach.area_of_expertise || '';
      const expertiseChips = expertise
        .split(',')
        .map((e: string) => e.trim())
        .filter((e: string) => expertiseOptions.includes(e));

      setFormData((prev:any) => ({
        ...prev,
        firstName: coach.first_name || '',
        lastName: coach.last_name || '',
        phone: coach.phone || '',
        location: coach.location || '',
        areaOfExpertise: expertiseChips.length === 0 ? expertise : '',
        expertiseChips: expertiseChips,
        experienceLevel: coach.notes || '', // Experience level stored in notes
        bio: coach.bio || '',
        avatar: coach.avatar || '',
      }));
    }
    // expertiseOptions is static and doesn't need to be in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachInfo]);

  const handleChipToggle = (chip: string) => {
    setStepError(null);
    setFormData((prev:any) => ({
      ...prev,
      expertiseChips: prev.expertiseChips.includes(chip)
        ? prev.expertiseChips.filter((c:any) => c !== chip)
        : [...prev.expertiseChips, chip],
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setStepError(null);
    setFormData((prev:any) => ({ ...prev, [name]: value }));
  };

  const validateStep = () => {
    if (currentStep === 1) {
      if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.phone.trim()) {
        return 'Please complete all required fields before continuing.';
      }
    }
    if (currentStep === 2) {
      const hasExpertise = formData.expertiseChips.length > 0 || formData.areaOfExpertise.trim();
      if (!hasExpertise) {
        return 'Please add at least one coaching expertise before continuing.';
      }
    }
    if (currentStep === 3) {
      if (!formData.experienceLevel.trim() || !formData.bio.trim()) {
        return 'Please complete your experience level and bio before continuing.';
      }
    }
    return null;
  };

  const saveProfile = async () => {
    const expertise = formData.expertiseChips.length > 0
      ? formData.expertiseChips.join(', ')
      : formData.areaOfExpertise;

    await dispatch(
      updateCoachProfile({
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        location: formData.location,
        area_of_expertise: expertise,
        experience_level: formData.experienceLevel,
        bio: formData.bio,
      })
    ).unwrap();
  };

  const handleNext = async () => {
    const error = validateStep();
    if (error) {
      setStepError(error);
      return;
    }

    if (currentStep === 3) {
      try {
        await saveProfile();
        setCurrentStep(4);
      } catch (err) {
        setStepError('Failed to save your profile. Please try again.');
      }
      return;
    }

    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    } else {
      handleFinish();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  useEffect(() => {
    setStepError(null);
  }, [currentStep]);

  // Upload handlers matching Settings pattern
  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = (file?: File) => {
    const selectedFile = file || (fileInputRef.current?.files?.[0] as File | undefined);
    if (!selectedFile) return;

    if (selectedFile.size > 2097152) {
      updateUploadState({ error: 'File size must be smaller than 2MB.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const previewUrl = (e.target?.result as string) || '';
      updateUploadState({
        file: selectedFile,
        previewUrl,
        error: '',
        zoom: 1,
        dragOffset: { x: 0, y: 0 },
      });
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleZoomWheel = (event: WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    const zoomChange = event.deltaY > 0 ? -0.1 : 0.1;
    setUploadState((prev) => ({
      ...prev,
      zoom: Math.max(1, Math.min(3, prev.zoom + zoomChange)),
    }));
  };

  const handlePreviewPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    setUploadState((prev) => ({
      ...prev,
      isDragging: true,
      dragOffset: { x: event.clientX, y: event.clientY },
    }));
  };

  const updateBaseImageSize = () => {
    if (previewImageRef.current) {
      setUploadState((prev) => ({
        ...prev,
        baseImageSize: {
          width: previewImageRef.current!.width,
          height: previewImageRef.current!.height,
        },
      }));
    }
  };

  const handleSavePhoto = async () => {
    if (!uploadState.file) return;
    try {
      const uploadResult = await dispatch(
        uploadProfileImage({ file: uploadState.file })
      ).unwrap();
      const uploadedUrl = uploadResult.fileUrl;
      setFormData((prev:any) => ({ ...prev, avatar: uploadedUrl }));
      // Update profile with the avatar URL immediately
      await dispatch(updateCoachProfile({ avatar: uploadedUrl })).unwrap();
      dispatch(getCoachInfo());
      closeUploadModal();
    } catch (error) {
      updateUploadState({ error: 'Failed to upload image' });
    }
  };

  const closeUploadModal = () => {
    updateUploadState({
      isOpen: false,
      file: null,
      error: '',
      previewUrl: '',
      zoom: 1,
      dragOffset: { x: 0, y: 0 },
    });
    dispatch(resetProfileImageUpload());
  };

  const handleOAuthConnect = async (provider: string) => {
    console.log('🔵 OAuth Connect clicked:', provider);
    
    // Disable Apple Calendar
    if (provider === 'apple') {
      setOauthError('Apple Calendar integration is not available yet');
      return;
    }

    try {
      setOauthLoading(provider);
      setOauthError(null);

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8082';
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const needsPrefix = !baseUrl.includes('localhost') && !baseUrl.includes('127.0.0.1') && !baseUrl.includes('/coaching/v1');
      const finalUrl = needsPrefix ? `${baseUrl}/coaching/v1` : baseUrl;
      const redirectUrl = `${window.location.origin}/onboarding?step=${currentStep}`;
      const authUrl = `${finalUrl}/api/integrations/${provider}/auth?redirect_url=${encodeURIComponent(redirectUrl)}`;
      
      console.log('🔵 Requesting auth URL:', authUrl);
      
      // Get OAuth auth URL from backend (using cookies for auth)
      const response = await fetch(authUrl, {
        credentials: 'include', // Send cookies
      });

      console.log('🔵 Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('🔴 Auth URL error:', errorData);
        throw new Error(errorData.error || `Failed to get auth URL for ${provider}`);
      }

      const data = await response.json();
      console.log('🔵 Auth URL response:', data);
      
      if (data.url) {
        // Store return URL in session storage
        sessionStorage.setItem('oauthReturnStep', String(currentStep));
        console.log('🔵 Redirecting to:', data.url);
        // Redirect to OAuth provider
        window.location.href = data.url;
      } else {
        setOauthError('Failed to get authorization URL');
        setOauthLoading(null);
      }
    } catch (err) {
      console.error('🔴 OAuth connection error:', err);
      setOauthError(err instanceof Error ? err.message : 'OAuth connection failed');
      setOauthLoading(null);
    }
  };

  const handleFinish = async () => {
    try {
      await saveProfile();

      // Clear localStorage on successful completion
      localStorage.removeItem('onboarding_step');
      localStorage.removeItem('onboarding_formData');

      // Fetch updated coach info
      await dispatch(getCoachInfo()).unwrap();
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Failed to save profile:', err);
    }
  };

  return (
    <div className="min-h-screen bg-white px-6 py-9">
      <div className="w-full max-w-3xl mx-auto">
       
        <div className="text-center mb-10">
          <h1 className="text-[24px] font-bold text-gray-900">Onefinnet Coaching</h1>
          <p className="text-[16px] text-gray-400 mt-2">Expert coaching, personalized growth</p>
        </div>

       
        {currentStep === 1 && (
          <div className="animate-fadeIn">
            <div className="mb-9">
              <h2 className="text-[20px] font-semibold text-gray-900 mb-3">
                Help us personalize your experience
              </h2>
              <p className="text-base text-gray-400">Tell us about yourself</p>
            </div>

            {stepError && (
              <div className="mb-5 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                {stepError}
              </div>
            )}

            <div className="space-y-6">
            
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 font-medium text-xl">
                    {formData.avatar ? (
                      <img
                        src={formData.avatar}
                        alt="Profile"
                        className="w-14 h-14 rounded-full object-cover"
                      />
                    ) : formData.firstName && formData.lastName ? (
                      `${formData.firstName[0]}${formData.lastName[0]}`.toUpperCase()
                    ) : (
                      <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className="text-base font-medium text-gray-900">
                      {formData.firstName && formData.lastName 
                        ? `${formData.firstName} ${formData.lastName}`
                        : formData.firstName || 'Floyd Miles'
                      }
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => updateUploadState({ isOpen: true })}
                  className="px-5 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  Edit Profile Picture
                </button>
              </div>

              {/* Form Fields */}
              <div className="space-y-6 pt-6 border-t border-gray-200">
                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      First Name<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="Enter first name"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Last Name<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Enter last name"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-gray-400"
                    />
                  </div>
                </div>

                {/* Contact and Location */}
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Contact<span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <select className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:border-gray-400">
                        <option>US</option>
                        <option>CA</option>
                        <option>UK</option>
                        <option>AU</option>
                      </select>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Type your number"
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-gray-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="Type your location here"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-gray-400"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

  
        {currentStep === 2 && (
          <div className="animate-fadeIn">
            <div className="mb-9">
              <h2 className="text-[20px] font-semibold text-gray-900 mb-1">
                What area do you expertise in?
              </h2>
              <p className="text-base text-gray-400">We need to understand what you can coach in!</p>
            </div>

            {stepError && (
              <div className="mb-5 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                {stepError}
              </div>
            )}

            <div className="space-y-8">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Coaching Expertise<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="areaOfExpertise"
                  value={formData.areaOfExpertise}
                  onChange={handleInputChange}
                  placeholder="Type what area do you expertise in"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-gray-400"
                />
              </div>

              <div className="flex flex-wrap gap-3">
                {expertiseOptions.map((option) => (
                  <button
                    key={option}
                    onClick={() => handleChipToggle(option)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                      formData.expertiseChips.includes(option)
                        ? 'bg-black text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

      
        {currentStep === 3 && (
          <div className="animate-fadeIn">
            <div className="mb-9">
              <h2 className="text-[20px] font-semibold text-gray-900 mb-1">
                Tell us about your journey
              </h2>
              <p className="text-base text-gray-400">
                This will help us find the students that are the best fit for you!
              </p>
            </div>

            {stepError && (
              <div className="mb-5 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                {stepError}
              </div>
            )}

            <div className="space-y-8">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-4">
                  How much experience do you have in Coaching?<span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-4">
                  {experienceLevels.map((level) => (
                    <button
                      key={level}
                      onClick={() => {
                        setStepError(null);
                        setFormData((prev:any) => ({ ...prev, experienceLevel: level }));
                      }}
                      className={`px-6 py-2 rounded-[12px] font-medium transition-all duration-200 ${
                        formData.experienceLevel === level
                          ? 'bg-black text-white border border-black'
                          : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Tell us about your journey as an expert in your field<span className="text-red-500">*</span>
                </label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  placeholder="Type your professional bio"
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-gray-400 resize-none"
                />
              </div>
            </div>
          </div>
        )}

        
        {currentStep === 4 && (
          <div className="animate-fadeIn">
            <div className="mb-9">
              <h2 className="text-[20px] font-semibold text-gray-900 mb-1">
                Finally connect your calendar
              </h2>
              <p className="text-base text-gray-400">
                This will help you keep track of all your reminders and meetings in your calendar!
              </p>
            </div>

            {stepError && (
              <div className="mb-5 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                {stepError}
              </div>
            )}

            {oauthError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {oauthError}
              </div>
            )}

            <div className="space-y-3">
              <div className="bg-white p-3 rounded-xl flex items-center justify-between border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect x="2" y="5" width="20" height="16" rx="2" fill="white" stroke="#4285F4" strokeWidth="1.5"/>
                      <rect x="2" y="5" width="20" height="5" rx="2" fill="#4285F4"/>
                      <text x="12" y="16" fontFamily="Arial, sans-serif" fontSize="8" fill="#4285F4" textAnchor="middle" fontWeight="bold">31</text>
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-900">Google Calendar</span>
                </div>
                <button
                  onClick={() => handleOAuthConnect('google')}
                  disabled={oauthLoading !== null}
                  className={`px-4 py-1.5 border border-gray-300 rounded-lg transition-colors text-sm text-gray-700 font-medium bg-white ${
                    oauthLoading === 'google'
                      ? 'cursor-wait opacity-75'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  {oauthLoading === 'google' ? 'Connecting...' : 'Connect'}
                </button>
              </div>

              <div className="bg-white p-3 rounded-xl flex items-center justify-between border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect width="24" height="24" rx="2" fill="#0078D4"/>
                      <circle cx="12" cy="12" r="5" fill="white"/>
                      <circle cx="12" cy="12" r="3" stroke="#0078D4" strokeWidth="1.5" fill="none"/>
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-900">Outlook Calendar</span>
                </div>
                <button
                  onClick={() => handleOAuthConnect('microsoft')}
                  disabled={oauthLoading !== null}
                  className={`px-4 py-1.5 border border-gray-300 rounded-lg transition-colors text-sm text-gray-700 font-medium bg-white ${
                    oauthLoading === 'microsoft'
                      ? 'cursor-wait opacity-75'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  {oauthLoading === 'microsoft' ? 'Connecting...' : 'Connect'}
                </button>
              </div>
            </div>
          </div>
        )}

       
        <div className="flex justify-between items-center mt-24 gap-4">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className="px-6 py-2.5 text-gray-400 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Back
          </button>
          <button
            onClick={handleNext}
            disabled={loading}
            className="px-10 py-2.5 bg-black text-white font-medium rounded-lg hover:bg-gray-800 disabled:opacity-75 transition-colors"
          >
            {loading ? 'Saving...' : currentStep === 4 ? 'Finish Setup' : 'Next'}
          </button>
        </div>

        <p className="text-xs text-gray-400 text-center mt-12">
          You can edit this information later in settings
        </p>

     
        <UploadModal
          data={{
            isOpen: uploadState.isOpen,
            previewUrl: uploadState.previewUrl,
            zoom: uploadState.zoom,
            isDragging: uploadState.isDragging,
            dragOffset: uploadState.dragOffset,
            uploadError: uploadState.error,
            isSaving: isProfileImageUploading,
            canSave: Boolean(uploadState.file),
            hasError: hasProfileImageUploadError || Boolean(uploadState.error),
            errorMessage: uploadState.error,
          }}
          refs={{
            fileInputRef,
            previewContainerRef,
            previewImageRef,
          }}
          actions={{
            onClose: closeUploadModal,
            onSave: handleSavePhoto,
            onChooseFile: handleChooseFile,
            onFileSelected: handleFileSelected,
            onZoomChange: (value: number) => updateUploadState({ zoom: value }),
            onZoomIn: () =>
              setUploadState((prev) => ({
                ...prev,
                zoom: Math.min(3, prev.zoom + 0.1),
              })),
            onZoomOut: () =>
              setUploadState((prev) => ({
                ...prev,
                zoom: Math.max(1, prev.zoom - 0.1),
              })),
            onWheel: handleZoomWheel,
            onPointerDown: handlePreviewPointerDown,
            onImageLoad: updateBaseImageSize,
          }}
        />
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default OnboardingSetup;
